import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, useState, useImperativeHandle, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { Spin, message, Button } from 'udesk-ui';
import styled from 'styled-components';
import Empty from '../empty';
import { getSummaryCallByCallId, postSummaryCallByCallId } from 'src/api/summary/call/{callId}';
import { putSummaryById } from 'src/api/summary/{id}';
import { getChatDataCallTaskByCallId } from 'src/api/chatDataCallTask/{callId}';
import { Field } from '../../const';
import MDEditor from 'src/components/common/MDEditor';

const Wrapper = styled.div``;

type TemplateProps = {
    callId?: number;
    taskId?: number;
    field?: Field;
    value?: string;
    onChange?: (v?: string) => void;
    isEdit?: boolean;
    forwardRef?: any;
    checkEditShow?: () => void;
    updateView?: () => void;
    thirdParty?: boolean;
    isCaseLibInAppeal?: boolean;
    sdkOptions?: any;
};

const Template: FC<TemplateProps> = (props) => {
    const {
        callId,
        taskId,
        field,
        value,
        onChange,
        isEdit,
        forwardRef,
        checkEditShow,
        updateView,
        isCaseLibInAppeal,
        sdkOptions,
    } = props;

    const [dataContent, setDataContent] = useState<string>();
    const [dataId, setDataId] = useState<number>();

    const { run: getData, loading: getSummaryLoading } = useRequest(
        () =>
            isCaseLibInAppeal
                ? new Promise((resolve, reject) => {
                      Udesk.business.sdkAjax
                          .get(
                              `open_api_v1/summary/call/${callId}?inspectTaskId=${taskId}&fieldId=${field?.id}`,
                              sdkOptions,
                              {
                                  props,
                              }
                          )
                          .then(
                              (res) => {
                                  resolve(res);
                              },
                              (reason) => {
                                  message.error(reason.errorMsg);
                                  reject(reason);
                              }
                          );
                  })
                : getSummaryCallByCallId({
                      params: { inspectTaskId: taskId!, fieldId: field?.id as number },
                      segments: { callId: callId! },
                  }),
        {
            ready: !!taskId && !!callId && !!field?.id,
            onSuccess: (res) => {
                onChange?.((res as any).data?.content);
                setDataId((res as any).data?.id);
                setDataContent((res as any).data?.content);
                checkEditShow?.();
                updateView?.();
            },
        }
    );

    const [getProgressLoading, setGetProgressLoading] = useState(false);

    const { run: getProgress, cancel: cancelGetSessionSummary } = useRequest(
        () =>
            getChatDataCallTaskByCallId({
                params: { inspectTaskId: taskId!, fieldId: field?.id as number },
                segments: { callId: callId! },
            }),
        {
            manual: true,
            pollingInterval: 3000,
            ready: !!taskId && !!callId && !!field?.id,
            onSuccess: (res) => {
                // 2-进行中 3-成功 4-失败
                if (res.data?.status === 3) {
                    cancelGetSessionSummary();
                    getData();
                    setGetProgressLoading(false);
                } else if (res.data?.status === 4) {
                    cancelGetSessionSummary();
                    message.error(
                        /* 生成失败 */ UdeskLocales['current'].components.businessSummary.components
                            .fieldItem.index.generationFailed
                    );
                    setGetProgressLoading(false);
                }
            },
            onError: () => {
                cancelGetSessionSummary();
            },
        }
    );

    const { run: genData, loading: genSummaryLoading } = useRequest(
        () =>
            postSummaryCallByCallId(
                { inspectTaskId: taskId, fieldId: field?.id },
                { segments: { callId: callId! } }
            ),
        {
            manual: true,
            ready: !!taskId && !!callId && !!field?.id,
            onSuccess: () => {
                setGetProgressLoading(true);
                getProgress();
            },
        }
    );

    const onGen = () => {
        genData();
    };

    const { run: putData, loading: putLoading } = useRequest(
        () => putSummaryById({ content: value }, { segments: { id: dataId! } }),
        {
            ready: !!dataId && !!dataContent,
            manual: true,
            onSuccess: () => {
                getData();
            },
        }
    );

    const isLaoding = useMemo(() => {
        return genSummaryLoading || getProgressLoading || getSummaryLoading || putLoading;
    }, [genSummaryLoading, getProgressLoading, getSummaryLoading, putLoading]);

    useImperativeHandle(
        forwardRef,
        () => {
            return {
                id: field?.id,
                putData: () => {
                    if (!isLaoding) putData();
                },
                genData: () => {
                    if (!isLaoding) genData();
                },
                getDataContent: () => {
                    return dataContent;
                },
            };
        },
        [field?.id, isLaoding, putData, genData, dataContent]
    );

    return (
        <Wrapper>
            <Spin spinning={isLaoding}>
                {dataContent ? (
                    <MDEditor isEdit={isEdit} value={value} onChange={onChange} />
                ) : (
                    <Empty>
                        {!props.thirdParty && !isCaseLibInAppeal && (
                            <Button onClick={() => onGen()} type="primary">
                                {/* 生成 */}
                                {
                                    UdeskLocales['current'].components.businessSummary.components
                                        .fieldItem.index.generate
                                }
                            </Button>
                        )}
                    </Empty>
                )}
            </Spin>
        </Wrapper>
    );
};

export default Template;
